import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Save } from "lucide-react";
import ChangePasswordCard from "../../components/ChangePasswordCard";
import PushNotificationCard from "../../components/PushNotificationCard";

export default function StudentProfile() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", bio: "", skill_level: 3.0, goals: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        bio: user.bio || "",
        skill_level: user.skill_level ?? 3.0,
        goals: user.goals || "",
      });
    }
  }, [user?.user_id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        bio: form.bio,
        skill_level: parseFloat(form.skill_level) || 3.0,
        goals: form.goals,
      };
      await api.patch("/me", payload);
      await refreshMe();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="student-profile-page">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Account</div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">Your profile</h1>
        <p className="text-sm text-[#A3A3A3] mt-3">
          Keep this up to date — your coach uses it to personalize lesson plans.
        </p>
      </div>

      <form onSubmit={save} className="card-surface p-8 space-y-6">
        <Field label="Full name" value={form.name} onChange={set("name")} required testId="profile-name-input" />
        <Field label="Email" type="email" value={form.email} onChange={set("email")} required testId="profile-email-input" />
        <Field label="Phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" testId="profile-phone-input" />

        <div>
          <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Current skill (DUPR, 1.0 – 6.0)</label>
          <input
            type="number" step="0.1" min="1.0" max="6.0"
            value={form.skill_level} onChange={set("skill_level")}
            className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
            data-testid="profile-skill-input"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Goals</label>
          <textarea
            rows={3} value={form.goals} onChange={set("goals")}
            placeholder="What do you want to work on?"
            className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-3 text-sm mt-1"
            data-testid="profile-goals-input"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Bio (optional)</label>
          <textarea
            rows={2} value={form.bio} onChange={set("bio")}
            placeholder="Tell your coach a bit about yourself"
            className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-3 text-sm mt-1"
            data-testid="profile-bio-input"
          />
        </div>

        <button disabled={saving} className="btn-primary py-3 px-6 text-xs inline-flex items-center gap-2" data-testid="profile-save-button">
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </form>

      <ChangePasswordCard />
      <PushNotificationCard />
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <input
        type={type} value={value} onChange={onChange} required={required} placeholder={placeholder}
        className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
        data-testid={testId}
      />
    </div>
  );
}
